import { useSlate } from 'slate-react';
import { Editor, Range, Element } from 'slate';
import { forwardRef, useState } from 'react';
import { insertBookmark } from 'slate/extensions/bookmark';
import { i18n } from 'shared/i18n';
import { useBookmarks } from 'entities/bookmark';
import { v4 as uuidv4 } from 'uuid';
import { useEditorLiveSection } from 'app/state/contexts/EditorUiContext';

interface BookmarkButtonProps {
  icon: string;
  css?: string;
  format?: string;
  text?: string;
}

export const BookmarkButton = forwardRef<HTMLButtonElement, BookmarkButtonProps>(({ icon, css }, ref) => {
  const editor = useSlate();
  const { selection } = editor;
  const hasSelectedText = selection && !Range.isCollapsed(selection);

  const [match] = Editor.nodes(editor, {
    at: editor.selection ?? undefined, // The current selection
    match: (n) => Element.isElement(n) && n.type === 'bookmark', // Check if the node type is 'bookmark'
    mode: 'all', // Iterate over all nodes in the selection
  });
  const isBookmarkInSelection = !!match;

  return (
    <button ref={ref} className={`${css}`} disabled={!hasSelectedText || isBookmarkInSelection}>
      <i className={`fa-regular fa-${icon}`} />
    </button>
  );
});

export const BookmarkContent = forwardRef<HTMLDivElement>((_, ref) => {
  const editor = useSlate();
  const [bookmarkLabel, setBookmarkLabel] = useState('');
  const { documentSuperId, superId: sectionId } = useEditorLiveSection();
  const { createBookmark } = useBookmarks(documentSuperId);

  const handleSubmit = async () => {
    if (bookmarkLabel === '') return;
    const bookmarkId = `bm-${uuidv4()}`;

    createBookmark({ bookmarkId, label: bookmarkLabel, documentSuperId, sectionId });
    insertBookmark(editor, bookmarkId, bookmarkLabel);
    setBookmarkLabel('');
  };

  return (
    <div
      ref={ref}
      style={{ display: 'flex', flexDirection: 'column', padding: '0.5rem 1rem 0.5rem 1rem' }}
      className={`bookmarkbar-content bookmark-button-wrapper`}
    >
      <p>{i18n('editor:subtoolbar.bookmark.label')}</p>
      <div className="bookmark-button-inner">
        <input
          type="text"
          value={bookmarkLabel}
          onChange={(e) => setBookmarkLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
          }}
        />
        <i className="fa-solid fa-check" onClick={handleSubmit} />
        <i className="fa-solid fa-x" onClick={() => setBookmarkLabel('')} />
      </div>
    </div>
  );
});
